import { Activity, BarChart3, Boxes, Flame, Gauge, LayoutGrid, type LucideIcon, PieChart, Sparkles, TrendingUp, Users } from 'lucide-react';
import { ASSIST_METRICS, ASSIST_TIME_BUCKETS, type AssistChartPlan, type AssistVizKind } from '@/shared/assist/AssistContract';
import type { DashboardAccent, DashboardTileConfig, DashboardTileViz } from '@/shared/assist/DashboardContract';
import type { AnalyticsField, AnalyticsFilter, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';
import type { VizAccent } from '@/design/VizAccents';
import type { TopicDefinition, TopicVizKind } from '@/shared/surfaces/TopicCatalogTypes';
import { assistFieldLabel, metricLabel } from '@/shared/analytics/MetricFormatting';

/**
 * Validation + conversion for the JSON tile config. A {@link DashboardTileConfig}
 * is the LLM-authorable, serializable shape; this module gates it (so a malformed
 * streamed/persisted config never crashes a tile) and re-derives the inputs the
 * EXISTING render components already expect — an {@link AssistChartPlan} for the
 * shared assist chart, and a minimal {@link TopicDefinition} for the cross-tab
 * matrix. No new tile bodies are introduced.
 */

const METRIC_VALUES = new Set<string>(ASSIST_METRICS.map((metric) => metric.value));
const BUCKET_VALUES = new Set<string>(ASSIST_TIME_BUCKETS);
const VIZ_VALUES = new Set<DashboardTileViz>(['ranked-bars', 'donut', 'trend', 'matrix', 'kpi', 'stat-band']);
const ACCENT_VALUES = new Set<DashboardAccent>(['indigo', 'teal', 'amber', 'violet', 'rose', 'cyan']);

/** Viz kinds whose data is a single group-by aggregate (rows of label → value). */
export const RANKED_VIZ = new Set<DashboardTileViz>(['ranked-bars', 'donut']);
/** Viz kinds that render a stat band (one or many headline numbers). */
export const STAT_VIZ = new Set<DashboardTileViz>(['kpi', 'stat-band']);

const ICONS: Record<string, LucideIcon> = {
    Activity,
    BarChart3,
    Boxes,
    Flame,
    Gauge,
    LayoutGrid,
    PieChart,
    Sparkles,
    TrendingUp,
    Users,
};

const DEFAULT_ICON: LucideIcon = Sparkles;
const DEFAULT_FIELD: AnalyticsField = 'task_category';
const DEFAULT_METRIC: AnalyticsMetric = 'turns';

/** Resolves a tile's string icon name to a lucide component (falling back to a default). */
export function tileIcon(name: string | undefined): LucideIcon {
    return name !== undefined && ICONS[name] !== undefined ? ICONS[name] : DEFAULT_ICON;
}

/** A tile's accent, defaulting to indigo. */
export function tileAccent(config: DashboardTileConfig): VizAccent {
    return (config.accent ?? 'indigo') as VizAccent;
}

/** The metrics a stat-band/kpi tile should show (config.metrics, or a single config.metric). */
export function tileStatMetrics(config: DashboardTileConfig): AnalyticsMetric[] {
    if (config.query.metrics !== undefined && config.query.metrics.length > 0) return config.query.metrics;
    return [config.query.metric ?? DEFAULT_METRIC];
}

/**
 * Re-derives an assist chart plan from a chart-kind tile so the shared
 * {@link SurfaceAssistChart} renderer can draw it (identical to how a pinned tile
 * re-derives its plan). Only valid for `ranked-bars`/`donut`/`trend`.
 */
export function chartPlanFromTile(config: DashboardTileConfig): AssistChartPlan {
    return {
        workflow: 'chart',
        title: config.title,
        narrative: '',
        viz: config.viz as AssistVizKind,
        primaryField: config.query.primaryField ?? DEFAULT_FIELD,
        metric: config.query.metric ?? DEFAULT_METRIC,
        bucket: config.query.bucket,
        filters: config.query.filters,
    };
}

/**
 * Synthesizes a minimal insight definition from a tile config so the cross-tab
 * matrix component (and any catalog-driven body) can render it. Mirrors
 * `definitionFromTile` in PinnedTileTypes.
 */
export function definitionFromDashboardTile(config: DashboardTileConfig): TopicDefinition {
    const primaryField = config.query.primaryField ?? DEFAULT_FIELD;
    const metric = config.query.metric ?? DEFAULT_METRIC;
    const lens = assistFieldLabel(primaryField).toLowerCase();
    return {
        id: config.id,
        title: config.title,
        hook: '',
        question: config.subtitle ?? '',
        icon: tileIcon(config.icon),
        accent: tileAccent(config),
        vizKind: dashboardVizToTopicViz(config.viz),
        primaryField,
        secondaryField: config.query.secondaryField,
        headlineMetric: metric,
        unitLabel: metricLabel(metric).toLowerCase(),
        lensLabel: lens,
        lensPlural: `${lens}s`,
        matrixRowLimit: config.query.limit,
        timeBucket: config.query.bucket,
    };
}

function dashboardVizToTopicViz(viz: DashboardTileViz): TopicVizKind {
    return viz === 'matrix' || viz === 'donut' || viz === 'trend' ? viz : 'ranked-bars';
}

function isFilterArray(value: unknown): value is AnalyticsFilter[] {
    return (
        Array.isArray(value) &&
        value.every(
            (entry) =>
                typeof entry === 'object' &&
                entry !== null &&
                typeof (entry as { field: unknown }).field === 'string' &&
                (entry as { field: string }).field.length > 0 &&
                Array.isArray((entry as { values: unknown }).values) &&
                (entry as { values: unknown[] }).values.every((item) => typeof item === 'string'),
        )
    );
}

function isOptionalField(value: unknown): boolean {
    return value === undefined || (typeof value === 'string' && value.length > 0);
}

function isOptionalMetric(value: unknown): boolean {
    return value === undefined || (typeof value === 'string' && METRIC_VALUES.has(value));
}

function isOptionalString(value: unknown): boolean {
    return value === undefined || typeof value === 'string';
}

function isValidQuery(viz: DashboardTileViz, query: Record<string, unknown>): boolean {
    if (!isOptionalField(query.primaryField)) return false;
    if (!isOptionalField(query.secondaryField)) return false;
    if (!isOptionalMetric(query.metric)) return false;
    if (query.metrics !== undefined && !(Array.isArray(query.metrics) && query.metrics.every((m) => typeof m === 'string' && METRIC_VALUES.has(m)))) return false;
    if (query.bucket !== undefined && (typeof query.bucket !== 'string' || !BUCKET_VALUES.has(query.bucket))) return false;
    if (query.limit !== undefined && (typeof query.limit !== 'number' || !Number.isFinite(query.limit) || query.limit <= 0)) return false;
    if (query.filters !== undefined && !isFilterArray(query.filters)) return false;
    if (!isOptionalString(query.sql)) return false;

    if (typeof query.sql === 'string' && query.sql.length > 0) return true;

    if (STAT_VIZ.has(viz)) {
        const hasMetric = typeof query.metric === 'string' && METRIC_VALUES.has(query.metric);
        const hasMetrics = Array.isArray(query.metrics) && query.metrics.length > 0;
        return hasMetric || hasMetrics;
    }
    if (viz === 'trend') {
        return typeof query.metric === 'string' && METRIC_VALUES.has(query.metric);
    }
    if (viz === 'matrix') {
        return typeof query.primaryField === 'string' && typeof query.secondaryField === 'string';
    }
    return typeof query.primaryField === 'string' && typeof query.metric === 'string' && METRIC_VALUES.has(query.metric);
}

/**
 * Whether a value is a structurally valid {@link DashboardTileConfig} with known
 * fields/metrics/viz. The defensive gate every streamed or persisted config must
 * pass before it is rendered.
 */
export function isValidDashboardTileConfig(value: unknown): value is DashboardTileConfig {
    if (typeof value !== 'object' || value === null) return false;
    const config = value as Record<string, unknown>;
    if (typeof config.id !== 'string' || config.id.length === 0) return false;
    if (typeof config.title !== 'string') return false;
    if (typeof config.viz !== 'string' || !VIZ_VALUES.has(config.viz as DashboardTileViz)) return false;
    if (config.accent !== undefined && (typeof config.accent !== 'string' || !ACCENT_VALUES.has(config.accent as DashboardAccent))) return false;
    if (config.icon !== undefined && typeof config.icon !== 'string') return false;
    if (config.subtitle !== undefined && typeof config.subtitle !== 'string') return false;
    if (config.span !== undefined && config.span !== 1 && config.span !== 2) return false;
    if (typeof config.query !== 'object' || config.query === null) return false;
    return isValidQuery(config.viz as DashboardTileViz, config.query as Record<string, unknown>);
}

/** A stable identity for a tile's data shape + composed filters, for cheap dedupe of repeated loads. */
export function aiExplorerTileKey(config: DashboardTileConfig, pageFilters: AnalyticsFilter[]): string {
    const q = config.query;
    const filters = [...pageFilters, ...(q.filters ?? [])]
        .map((filter) => `${filter.field}=${[...filter.values].sort().join('|')}`)
        .sort()
        .join(';');
    if (typeof q.sql === 'string' && q.sql.length > 0) return `sql:${config.viz}:${q.sql}|${filters}`;
    return [config.viz, q.primaryField ?? '', q.secondaryField ?? '', q.metric ?? '', (q.metrics ?? []).join(','), q.bucket ?? '', q.limit ?? '', filters].join('|');
}
